import axios from 'axios';
import { refreshAccessToken, getStoredAccessToken, clearStoredTokens } from './auth/auth.service';

const baseUrl = window.origin;

const api = axios.create({
  baseURL: `${baseUrl}/api`,
});

// Variable pour éviter les appels multiples de refresh simultanés
let isRefreshing = false;

let failedQueue: Array<{
  resolve: (value: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  
  failedQueue = [];
};

// Intercepteur pour ajouter automatiquement le token d'authentification
api.interceptors.request.use(
  async (config) => {
    // Ne pas intercepter les requêtes de refresh pour éviter la boucle infinie
    if (config.url?.includes('/auth/refresh')) {
      console.log('🔄 Requête de refresh token, pas d\'interception');
      return config;
    }
    
    let token = getStoredAccessToken();
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log(`📤 Requête ${config.method?.toUpperCase()} ${config.url} avec token`);
    } else {
      console.warn(`⚠️ Requête ${config.method?.toUpperCase()} ${config.url} SANS token`);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Intercepteur pour gérer les erreurs d'authentification et renouveler les tokens
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Ne pas intercepter les erreurs des requêtes de refresh pour éviter la boucle infinie
    if (originalRequest.url?.includes('/auth/refresh')) {
      console.error('❌ Erreur lors du refresh token, déconnexion');
      clearStoredTokens();
      window.location.href = '/login';
      return Promise.reject(error);
    }

    // Ne pas intercepter les erreurs de login ou d'inscription
    if (originalRequest.url?.includes('/auth/login') || originalRequest.url?.includes('/auth/inscription')) {
      console.log('❌ Erreur lors du login/inscription, pas de refresh');
      return Promise.reject(error);
    }

    // Ne pas intercepter les erreurs 403 qui sont liées à un email non vérifié
    if (error.response?.status === 403 && error.response?.data?.emailNotVerified) {
      console.log('⚠️ Email non vérifié, pas de tentative de refresh');
      return Promise.reject(error);
    }

    // Si erreur 401 (Unauthorized) ou 403 (Forbidden, peut indiquer un token expiré) et pas déjà une tentative de retry
    if ((error.response?.status === 401 || error.response?.status === 403) && !originalRequest._retry) {
      console.log('🔄 Erreur 401/403 détectée, tentative de refresh du token...');
      
      if (isRefreshing) {
        // Si un refresh est déjà en cours, mettre la requête en queue
        console.log('⏳ Refresh en cours, ajout à la queue');
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          if (token) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          }
          return Promise.reject(error);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Tenter de rafraîchir le token
        console.log('🔄 Rafraîchissement du token en cours...');
        await refreshAccessToken();
        const newToken = getStoredAccessToken();
        
        if (newToken) {
          console.log('✅ Token rafraîchi avec succès');
          // Traiter la queue avec le nouveau token
          processQueue(null, newToken);
          
          // Retry la requête originale avec le nouveau token
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        } else {
          // Pas de nouveau token, déconnecter
          throw new Error('Impossible de renouveler le token');
        }
      } catch (refreshError) {
        // Erreur lors du refresh, traiter la queue avec l'erreur
        console.error('❌ Échec du refresh token:', refreshError);
        processQueue(refreshError, null);
        
        // Déconnecter l'utilisateur
        clearStoredTokens();
       
       
        window.location.href = '/login';
        
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export const get = (endpoint: string, config = {}) => {
  return api.get(endpoint, config);
};

export const post = (endpoint: string, data: any, config = {}) => {
  return api.post(endpoint, data, config);
};

export const put = (endpoint: string, data: any, config = {}) => {
  return api.put(endpoint, data, config);
};

export const del = (endpoint: string, config = {}) => {
  return api.delete(endpoint, config);
};

export const patch = (endpoint: string, data: any, config = {}) => {
  return api.patch(endpoint, data, config);
};

export { api };
